import { EntityEditorControllerApiFactory, type IndexTicker, type ReviewTicker } from "@eu.mdotm.api/sphere";
import { Banner, Text } from "@mdotm/shadcn/legacy/components";
import { Map, Set } from "immutable";
import { useCallback, useMemo, useState } from "react";
import { flushSync } from "react-dom";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router";
import { match } from "ts-pattern";
import { useFunctionalAreas } from "$root/App/context";
import { runWithErrorReporting } from "$root/api/error-reporting/report";
import { useApiGen } from "$root/api/hooks";
import { useCrumbs } from "$root/components/Crumbs/useCrumbs";
import { LeavePrompt } from "$root/components/LeavePrompt";
import { typedUrlForRoute } from "$root/components/PlatformRouter/RoutesDef";
import { ReactQueryWrapperBase } from "$root/components/ReactQueryWrapper";
import { aclByArea } from "$root/functional-areas/acl/checkers/all";
import { referenceRepository } from "$root/functional-areas/portfolio/references-repository";
import { portfolioRepository } from "$root/functional-areas/portfolio/repository";
import { SmallInvestmentSummary } from "$root/functional-areas/portfolio/SmallInvestmentSummary";
import { QuantityReviewContainer } from "$root/functional-areas/proxies/quantity-review/QuantityReviewContainer";
import { ReviewHeader } from "$root/functional-areas/proxies/ReviewHeader";
import { useReviewCompositionQueries } from "$root/functional-areas/proxies/reviewQueries";
import { WeightReviewContainer } from "$root/functional-areas/proxies/WeightReviewContainer";
import { universeRepository } from "$root/functional-areas/universe/repository";
import { SmallUniverseSummary } from "$root/functional-areas/universe/SmallUniverseSummary";
import { useUserValue } from "$root/functional-areas/user";
import { useQueryNoRefetch } from "$root/third-party-integrations/react-query/hooks";
import { Card } from "$root/widgets-architecture/layout/Card";
import { PortfolioStudioTab } from "./PortfoliosStudio/portfolio-studio-tabs";
import { getPortfolioStudioUrl } from "./PortfoliosStudio/usePortfolioStudioRedirect";
import type { ReviewEntity } from "./ReviewInstruments.const";

export type ProxyBuilder = {
	proxied: Map<string, Array<IndexTicker>>;
	deleted: Set<string>;
	identifierMap: Map<string /** tickerName */, string /** isin, cusip, bloomberg ticker */>;
	isDirty: boolean;
};

export const proxyBuilderDefault = {
	deleted: Set(),
	proxied: Map(),
	identifierMap: Map(),
	isDirty: false,
} satisfies ProxyBuilder;

const ReviewComposition = (): React.JSX.Element => {
	const [isDirty, setIsDirty] = useState<boolean>(false);
	const [pathToNotBlock, setPathToNotBlock] = useState<string[]>([typedUrlForRoute("Login", {})]);

	const { t } = useTranslation();
	const history = useHistory();
	const { uuid, entity, workspaceUid } = useParams<{
		uuid: string;
		entity: ReviewEntity;
		workspaceUid?: string;
	}>();

	const crumbs = useCrumbs();
	const workspacePath = crumbs.useWorkspacePath(workspaceUid);
	const user = useUserValue();

	const entityEditorApi = useApiGen(EntityEditorControllerApiFactory);

	const querySummary = useQueryNoRefetch(["queryReviewCompositionSummary", uuid], {
		queryFn() {
			return match(entity)
				.with("INVESTMENT", "TARGET_INVESTMENT", async () => {
					const response = await portfolioRepository.summary.fetchQuery(uuid, { enhanced: false });
					return {
						name: response.name,
						richAcl: response.richAcl,
						status: response.status,
						quantityMode: response.quantityMode ?? false,
						baseCurrency: response.baseCurrency,
					};
				})
				.with("BENCHMARK", async () => {
					const response = await referenceRepository.benchmarkSummary.fetchQuery(uuid);
					return {
						name: response.name,
						richAcl: response.richAcl,
						status: response.status,
						quantityMode: false,
						baseCurrency: undefined,
					};
				})
				.with("UNIVERSE", async () => {
					const response = await universeRepository.summary.fetchQuery(uuid);
					return {
						name: response.name,
						richAcl: response.richAcl,
						status: response.status,
						quantityMode: false,
						baseCurrency: undefined,
					};
				})
				.exhaustive();
		},
	});

	const { data: summary } = querySummary;
	// Started with the summary rather than once the view is known; each view reads it from the cache.
	useReviewCompositionQueries(uuid, entity);

	// Expose the composition review step as Sphere AI functional-area context, so the
	// assistant knows which entity's proxies the user is resolving and can drive the
	// confirm-then-apply review write tools (see the review descriptor in appContext).
	useFunctionalAreas(
		() => ({ areas: "review", data: { uuid, entity, name: summary?.name } }),
		[uuid, entity, summary?.name],
	);

	const forwardingPage = useMemo(
		() =>
			match(entity)
				.with("INVESTMENT", () =>
					workspaceUid
						? typedUrlForRoute("PortfolioDetailsWithWorkspace", {
								workspaceUid,
								portfolioUid: uuid,
								proposal: String(summary?.status === "PROPOSAL_READY"),
							})
						: typedUrlForRoute("PortfolioDetails", {
								portfolioUid: uuid,
								proposal: String(summary?.status === "PROPOSAL_READY"),
							}),
				)
				.with("TARGET_INVESTMENT", () => typedUrlForRoute("ReferenceDetails", { uuid }))
				.with("BENCHMARK", () => typedUrlForRoute("LegacyBenchmark", { uuid }))
				.with("UNIVERSE", () => typedUrlForRoute("UniverseDetails", { universeUuid: uuid }))
				.exhaustive(),
		[entity, summary?.status, uuid, workspaceUid],
	);

	const forwardingToBackPage = useMemo(
		() =>
			match(entity)
				.with("INVESTMENT", () => getPortfolioStudioUrl())
				.with("TARGET_INVESTMENT", () =>
					typedUrlForRoute("PortfoliosStudio", {
						tab: PortfolioStudioTab.References,
					}),
				)
				.with("BENCHMARK", () =>
					typedUrlForRoute("PortfoliosStudio", {
						tab: PortfolioStudioTab.References,
					}),
				)
				.with("UNIVERSE", () =>
					typedUrlForRoute("PortfoliosStudio", {
						tab: PortfolioStudioTab.Universes,
					}),
				)
				.exhaustive(),
		[entity],
	);

	const canEditComposition = useMemo(
		() =>
			summary?.richAcl?.acl ? aclByArea.portfolio.canEditComposition(user.id, summary?.richAcl?.acl ?? []) : false,
		[summary?.richAcl?.acl, user.id],
	);

	const onSave = useCallback(
		async (composition: ReviewTicker[]) => {
			await runWithErrorReporting(
				async () => {
					await entityEditorApi.saveEditorReviewEntity(entity, {
						identifier: uuid,
						tickerComposition: composition,
					});
					flushSync(() => setPathToNotBlock([forwardingPage]));
					history.push(forwardingPage);
				},
				{
					area: "portfolio",
					attemptedOperation: {
						message: `submit reviewed composition "${uuid}"`,
						payload: JSON.stringify(composition),
					},
				},
			);
		},
		[entity, entityEditorApi, forwardingPage, history, uuid],
	);

	const onDirty = useCallback(() => setIsDirty(true), []);

	const header = {
		crumbs: match(entity)
			.with("INVESTMENT", () => [
				...crumbs.portfolioStudio("Portfolios", uuid),
				...workspacePath,
				...crumbs.portfolioName({ name: summary?.name, uuid }),
				...crumbs.reviewInstrument,
			])
			.with("TARGET_INVESTMENT", () => [
				...crumbs.portfolioStudio("References", uuid),
				...crumbs.referenceName({ name: summary?.name, uuid }),
				...crumbs.reviewInstrument,
			])
			.with("BENCHMARK", () => [
				...crumbs.portfolioStudio("References", uuid),
				...crumbs.benchmarkName({ name: summary?.name, uuid }),
				...crumbs.reviewInstrument,
			])
			.with("UNIVERSE", () => [
				...crumbs.portfolioStudio("Universes", uuid),
				...crumbs.universeName({ name: summary?.name, uuid }),
				...crumbs.reviewInstrument,
			])
			.exhaustive(),
		onCancel: () => history.push(forwardingToBackPage),
	};

	return (
		<>
			{summary == null && <ReviewHeader {...header} />}
			<ReactQueryWrapperBase query={querySummary}>
				{({ quantityMode, baseCurrency }) => (
					<>
						{match(entity)
							.with("INVESTMENT", () => (
								<div className="mb-5">
									<SmallInvestmentSummary enhanced={false} portfolio={{ reference: false, uuid }} />
								</div>
							))
							.with("TARGET_INVESTMENT", () => (
								<div className="mb-5">
									<SmallInvestmentSummary enhanced={false} portfolio={{ reference: true, uuid }} />
								</div>
							))
							.with("BENCHMARK", () => <></>)
							.with("UNIVERSE", () => (
								<div className="mb-5">
									<SmallUniverseSummary universe={{ uuid }} />
								</div>
							))
							.exhaustive()}

						<Card>
							<Text type="Body/XL/Bold" className="mb-4">
								Composition
							</Text>

							<Banner className="mb-4" severity="warning" title="Review Before Saving">
								Some instruments require your attention. This may be due to missing information or because they have
								been delisted. Please review the list below and make any necessary updates before saving.
							</Banner>
							{quantityMode ? (
								<QuantityReviewContainer
									uuid={uuid}
									entity={entity}
									currency={baseCurrency}
									canEditComposition={canEditComposition}
									header={header}
									onSave={onSave}
									onDirty={onDirty}
									onLeave={() => history.push(forwardingToBackPage)}
								/>
							) : (
								<WeightReviewContainer
									uuid={uuid}
									entity={entity}
									canEditComposition={canEditComposition}
									header={header}
									onSave={onSave}
									onDirty={onDirty}
								/>
							)}
						</Card>
					</>
				)}
			</ReactQueryWrapperBase>
			<LeavePrompt title={t("LEAVE_PAGE")} when={isDirty} pathToNotBlock={pathToNotBlock}>
				{t("EDIT_COMPOSITION.LEAVE")}
			</LeavePrompt>
		</>
	);
};

/** @internal — exported for tests */
export {
	convertReviewInstrumentToReviewTicker,
	hasBeenDeleted,
	hasBeenReviewed,
	needsReview,
	sanitizeReviewInstrument,
} from "$root/functional-areas/proxies/reviewRows";

export default ReviewComposition;
